import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAraAuth } from './AraAuthContext';

/*
 * AraActiveSessionContext — in-memory session continuity for Ara.
 * Mirrors Vela's ActiveSessionContext pattern. Never writes to any
 * persistent storage. Cleared on Ara logout.
 */

const AraActiveSessionContext = createContext(null);

export const useAraActiveSession = () => {
  const ctx = useContext(AraActiveSessionContext);
  if (!ctx) throw new Error('useAraActiveSession must be used within AraActiveSessionProvider');
  return ctx;
};

const freshIdentity = () => ({
  sessionId: `ara_${Date.now()}`,
  sessionStartTime: new Date().toISOString(),
});

export const AraActiveSessionProvider = ({ children }) => {
  const { isAuthenticated } = useAraAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionSaved, setSessionSaved] = useState(false);
  const [identity, setIdentity] = useState(freshIdentity);

  const sessionIdRef = useRef(identity.sessionId);
  useEffect(() => {
    sessionIdRef.current = identity.sessionId;
  }, [identity.sessionId]);

  const discardSession = useCallback(() => {
    setMessages([]);
    setInput('');
    setIsLoading(false);
    setSessionSaved(false);
    setIdentity(freshIdentity());
  }, []);

  // Load a stored conversation (from AraSanctuaryDB SESSIONS) into memory
  // so returning users continue where they left off. Does NOT write.
  const restoreSession = useCallback((stored) => {
    if (!stored || !stored.id || !Array.isArray(stored.messages)) return;
    setMessages(stored.messages);
    setInput('');
    setIsLoading(false);
    setSessionSaved(false);
    setIdentity({
      sessionId: stored.id,
      sessionStartTime: stored.startedAt || new Date().toISOString(),
    });
  }, []);

  const wasAuthenticatedRef = useRef(isAuthenticated);
  useEffect(() => {
    if (wasAuthenticatedRef.current && !isAuthenticated) {
      setMessages([]);
      setInput('');
      setIsLoading(false);
      setSessionSaved(false);
      setIdentity(freshIdentity());
    }
    wasAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated]);

  return (
    <AraActiveSessionContext.Provider
      value={{
        messages, setMessages,
        input, setInput,
        isLoading, setIsLoading,
        sessionSaved, setSessionSaved,
        sessionId: identity.sessionId,
        sessionStartTime: identity.sessionStartTime,
        sessionIdRef,
        discardSession,
        restoreSession,
      }}
    >
      {children}
    </AraActiveSessionContext.Provider>
  );
};
